import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Download, Send, CheckCircle2, XCircle, ShoppingCart, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money, fdate, downloadPdf, ROLES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const Steps = ({ status }) => {
  const steps = [["brouillon", "Devis interne"], ["soumis", "Validation direction"], ["approuve", "Validé"], ["commande", "Commande"]];
  const idx = { brouillon: 0, rejete: 1, soumis: 1, approuve: 2, commande: 3 }[status];
  return (
    <ol className="mb-6 grid grid-cols-4 gap-2" data-testid="quote-workflow-steps">
      {steps.map(([k, l], i) => (
        <li key={k} className={`border-t-4 pt-2 text-xs font-semibold uppercase tracking-wider ${i <= idx ? (status === "rejete" && i === 1 ? "border-red-600 text-red-600" : "border-blue-900 text-slate-900") : "border-slate-200 text-slate-400"}`}>
          {status === "rejete" && i === 1 ? "Rejeté" : l}
        </li>
      ))}
    </ol>
  );
};

export default function QuoteDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState(null);
  const [comment, setComment] = useState("");

  const load = () => api.get(`/quotes/${id}`).then((r) => setQ(r.data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (path, msg, body) => {
    try {
      const r = await api.post(`/quotes/${id}/${path}`, body);
      toast.success(msg);
      if (path === "convert") return navigate("/commandes");
      setQ(r.data);
      setComment("");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer ce brouillon ?")) return;
    await api.delete(`/quotes/${id}`);
    toast.success("Devis supprimé");
    navigate("/devis");
  };

  if (!q) return <p className="text-sm text-slate-500">Chargement…</p>;
  const isAdmin = user.role === "admin";
  const editable = ["brouillon", "rejete"].includes(q.status);

  return (
    <div>
      <PageHeader kicker={`Devis ${q.number}`} title={q.client_name}>
        <Button variant="outline" data-testid="quote-pdf-btn" onClick={() => downloadPdf(`/quotes/${id}/pdf`, `${q.number}.pdf`)}><Download className="mr-1 h-4 w-4" /> PDF</Button>
        {editable && <Button variant="outline" data-testid="quote-edit-btn" onClick={() => navigate(`/devis/${id}/modifier`)}><Pencil className="mr-1 h-4 w-4" /> Modifier</Button>}
        {q.status === "brouillon" && <Button variant="outline" data-testid="quote-delete-btn" onClick={remove}><Trash2 className="h-4 w-4" /></Button>}
        {editable && <Button data-testid="quote-submit-btn" className="bg-amber-600 hover:bg-amber-700" onClick={() => act("submit", "Devis soumis à la direction")}><Send className="mr-1 h-4 w-4" /> Soumettre à la direction</Button>}
        {q.status === "approuve" && <Button data-testid="quote-convert-btn" className="bg-blue-900 hover:bg-blue-950" onClick={() => act("convert", "Commande créée")}><ShoppingCart className="mr-1 h-4 w-4" /> Transformer en commande</Button>}
      </PageHeader>
      <Steps status={q.status} />
      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        <div className="space-y-6">
          <Panel title="Détails" action={<StatusBadge status={q.status} testId="quote-status-badge" />}>
            <dl className="grid gap-4 text-sm sm:grid-cols-2">
              <div><dt className="text-xs uppercase tracking-wider text-slate-500">Objet</dt><dd className="mt-1 font-medium">{q.object}</dd></div>
              <div><dt className="text-xs uppercase tracking-wider text-slate-500">Contact</dt><dd className="mt-1">{q.client_contact || "—"}</dd></div>
              <div><dt className="text-xs uppercase tracking-wider text-slate-500">Adresse</dt><dd className="mt-1">{q.client_address || "—"}</dd></div>
              <div><dt className="text-xs uppercase tracking-wider text-slate-500">Créé le / par</dt><dd className="mt-1">{fdate(q.created_at)} · {q.created_by}</dd></div>
              {q.order_number && <div><dt className="text-xs uppercase tracking-wider text-slate-500">Commande</dt><dd className="mt-1"><Link to="/commandes" className="font-mono text-blue-900 underline">{q.order_number}</Link></dd></div>}
              {q.validation_comment && <div className="sm:col-span-2"><dt className="text-xs uppercase tracking-wider text-slate-500">Commentaire direction</dt><dd className="mt-1" data-testid="quote-validation-comment">{q.validation_comment}</dd></div>}
            </dl>
          </Panel>
          <Panel title="Lignes">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500"><th className="pb-2">Désignation</th><th className="pb-2 text-right">Qté</th><th className="pb-2 text-right">P.U.</th><th className="pb-2 text-right">Montant</th></tr></thead>
              <tbody>
                {q.lines.map((l, i) => (
                  <tr key={i} className="border-t border-slate-100"><td className="py-2">{l.description}</td><td className="py-2 text-right">{l.quantity}</td><td className="py-2 text-right">{money(l.unit_price)}</td><td className="py-2 text-right font-medium">{money(l.quantity * l.unit_price)}</td></tr>
                ))}
              </tbody>
            </table>
            <dl className="ml-auto mt-4 max-w-xs space-y-1 text-sm">
              <div className="flex justify-between"><dt className="text-slate-500">Total HT</dt><dd>{money(q.subtotal)}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">TVA ({q.tva_rate}%)</dt><dd>{money(q.tva)}</dd></div>
              <div className="flex justify-between border-t-2 border-red-600 pt-1 font-display text-lg font-bold"><dt>Total TTC</dt><dd data-testid="quote-detail-total">{money(q.total)}</dd></div>
            </dl>
          </Panel>
        </div>
        <div className="space-y-6">
          {isAdmin && q.status === "soumis" && (
            <Panel title="Décision de la direction" className="border-amber-300">
              <Textarea placeholder="Commentaire (optionnel)" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="quote-decision-comment" />
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Button data-testid="quote-approve-btn" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => act("approve", "Devis validé par la direction", { comment })}><CheckCircle2 className="mr-1 h-4 w-4" /> Valider</Button>
                <Button data-testid="quote-reject-btn" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => act("reject", "Devis rejeté", { comment })}><XCircle className="mr-1 h-4 w-4" /> Rejeter</Button>
              </div>
            </Panel>
          )}
          {!isAdmin && q.status === "soumis" && <p className="rounded-md bg-amber-50 p-4 text-sm text-amber-800">En attente de validation par la direction.</p>}
          <Panel title="Historique">
            <ol className="space-y-4 border-l border-slate-200 pl-4" data-testid="quote-history">
              {q.history.map((h, i) => (
                <li key={i} className="relative text-sm">
                  <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-red-600" />
                  <p className="font-medium">{h.action}</p>
                  <p className="text-xs text-slate-500">{new Date(h.at).toLocaleString("fr-FR")} · {h.by} ({ROLES[h.role]})</p>
                  {h.comment && <p className="mt-1 text-xs italic text-slate-600">« {h.comment} »</p>}
                </li>
              ))}
            </ol>
          </Panel>
        </div>
      </div>
    </div>
  );
}
